import type { InitSessionConfigStep, QuestionConfigStep } from '@types';
import type { SessionAnswer } from './sessionAnswers';

export const getQuestionStepEntries = (
  config: InitSessionConfigStep[]
): Array<{ configIndex: number; step: QuestionConfigStep }> =>
  config.flatMap((step, configIndex) =>
    step !== null &&
    typeof step === 'object' &&
    'question' in step &&
    typeof (step as QuestionConfigStep).question === 'string'
      ? [{ configIndex, step: step as QuestionConfigStep }]
      : []
  );

/**
 * Risolve il valore grezzo salvato a fine sessione (es. "1", indice 1-based)
 * nel testo dell'opzione definito in config, se possibile.
 */
export const resolveRawSelectionToAnswerLabel = (
  step: QuestionConfigStep,
  raw: string
): string => {
  const trimmed = raw.trim();
  if (/^-?\d+$/.test(trimmed)) {
    const n = Number.parseInt(trimmed, 10);
    if (n >= 1 && n <= step.answers.length) {
      return step.answers[n - 1];
    }
    if (n >= 0 && n < step.answers.length) {
      return step.answers[n];
    }
  }
  const exact = step.answers.find(
    a => a.trim().toLowerCase() === trimmed.toLowerCase()
  );
  if (exact !== undefined) {
    return exact;
  }
  return raw;
};

/**
 * Abbina in ordine i valori primitivi (es. da sessionEnded) agli step "question" della config.
 */
export const mapPrimitiveValuesToSessionAnswers = (
  values: Array<string | number | boolean>,
  config: InitSessionConfigStep[],
  getTimestamp: (index: number) => string
): SessionAnswer[] => {
  const entries = getQuestionStepEntries(config);
  return values.map((value, j) => {
    const raw = String(value);
    const entry = entries[j];
    if (entry !== undefined) {
      return {
        question: entry.step.question,
        answer: resolveRawSelectionToAnswerLabel(entry.step, raw),
        stepId: entry.configIndex,
        timestamp: getTimestamp(j),
      };
    }
    return {
      question: `Risposta ${j + 1}`,
      answer: raw,
      stepId: j + 1,
      timestamp: getTimestamp(j),
    };
  });
};
